import CryptoJS from 'crypto-js';
import { ENV } from "../main/config";
class EncryptionUtils {
    private static encryptionKey: string;
    static initialize(): void {
        let key = ENV.encryptionKey || 'auditzen-default-encryption-key-32-chars';
        if (key.length < 32) key = key.padEnd(32, '0');
        if (key.length > 32) key = key.substring(0, 32);
        this.encryptionKey = key;
    }
    static encrypt(data: any): string {
        if (!this.encryptionKey) this.initialize();
        try {
            const stringData = typeof data === 'string' ? data : JSON.stringify(data);
            return CryptoJS.AES.encrypt(stringData, this.encryptionKey).toString();
        } catch (error) {
            console.error("Encryption error:", error);
            throw new Error("Failed to encrypt data");
        }
    }
    static decrypt<T = any>(encryptedData: string): T | null {
        if (!this.encryptionKey) this.initialize();
        try {
            const bytes = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
            const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
            if (!decryptedString) return null;
            try {
                return JSON.parse(decryptedString) as T;
            } catch {
                return decryptedString as T;
            }
        } catch (error) {
            console.error("Decryption error:", error);
            return null;
        }
    }
    static setLocalStorage(key: string, value: any): void {
        const encryptedValue = this.encrypt(value);
        localStorage.setItem(key, encryptedValue);
    }
    static getLocalStorage<T = any>(key: string): T | null {
        const encryptedValue = localStorage.getItem(key);
        if (!encryptedValue) return null;
        return this.decrypt<T>(encryptedValue);
    }
    static removeLocalStorage(key: string): void {
        localStorage.removeItem(key);
    }
    static hasLocalStorage(key: string): boolean {
        return localStorage.getItem(key) !== null;
    }
    static clearLocalStorage(pattern?: string): void {
        if (!pattern) {
            localStorage.clear();
            return;
        }
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.includes(pattern)) {
                localStorage.removeItem(key);
            }
        }
    }
    static generateHash(data: any): string {
        const stringData = typeof data === 'string' ? data : JSON.stringify(data);
        return CryptoJS.SHA256(stringData).toString();
    }
    static setWithExpiry(key: string, value: any, ttlMs: number): void {
        const item = {
            value,
            expiry: Date.now() + ttlMs
        };
        this.setLocalStorage(key, item);
    }
    static getWithExpiry<T = any>(key: string): T | null {
        const item = this.getLocalStorage<{ value: T; expiry: number }>(key);
        if (!item) return null;
        if (Date.now() > item.expiry) {
            this.removeLocalStorage(key);
            return null;
        }
        return item.value;
    }
    static get<T = any>(key: string): T | null {
        const encrypted = localStorage.getItem(key);
        if (!encrypted) return null;
        return this.decrypt<T>(encrypted);
    }
}
EncryptionUtils.initialize();
export default EncryptionUtils;